import React, { useEffect, useState } from 'react';
import { Alert, Button, Card, Form, Input, InputNumber, Space, Spin, Switch, Tag, Typography, message } from 'antd';
import { ApiOutlined, ReloadOutlined, SaveOutlined } from '@ant-design/icons';
import { useAuth } from '../contexts/AuthContext';
import api from '../utils/api';
import { useUnsavedChanges } from '../components/NavigationGuard';

function JobSourceSettings({ embedded = false, onSaved }) {
  const { user } = useAuth();
  const isAdmin = user?.role === 'super_admin';
  const [form] = Form.useForm();
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [error, setError] = useState('');
  const [testResult, setTestResult] = useState(null);
  const [reload, setReload] = useState(0);
  const [dirty, setDirty] = useState(false);
  useUnsavedChanges(dirty);

  useEffect(() => {
    if (!isAdmin) { setLoading(false); return; }
    let cancelled = false;
    setLoading(true);
    setError('');
    api.get('/job-source').then(response => {
      if (!cancelled) {
        setSettings(response.data);
        form.setFieldsValue({ ...response.data, apiKey: '' });
        setDirty(false);
      }
    }).catch(requestError => { if (!cancelled) setError(requestError.response?.data?.message || '加载配置失败，请重试'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [isAdmin, form, reload]);

  if (!isAdmin) return <Alert type="error" showIcon message="仅超级管理员可以配置岗位数据源" />;

  const save = async values => {
    try {
      setSaving(true);
      setError('');
      const response = await api.put('/job-source', values);
      setSettings(response.data);
      form.setFieldsValue({ apiKey: '' });
      setTestResult(null);
      setDirty(false);
      message.success('岗位数据源已保存');
      onSaved?.();
    } catch (requestError) { setError(requestError.response?.data?.message || '保存失败，请重试'); }
    finally { setSaving(false); }
  };

  const test = async () => {
    try {
      const values = await form.validateFields();
      setTesting(true);
      setTestResult(null);
      const response = await api.post('/job-source/test', values);
      setTestResult(response.data);
    } catch (requestError) {
      if (!requestError.errorFields) setTestResult({ success: false, message: requestError.response?.data?.message || '连接测试失败，请重试' });
    } finally { setTesting(false); }
  };

  return (
    <div>
      {embedded ? <Typography.Paragraph type="secondary">全系统共用一套实时岗位接口，岗位资料在源系统中维护。</Typography.Paragraph> : <div className="page-header">
        <Typography.Title level={2}>岗位数据源设置</Typography.Title>
        <div className="description">全系统共用一套实时岗位接口，岗位资料在源系统中维护。</div>
      </div>}
      {error && <Alert type="error" showIcon message={error} style={{ marginBottom: 16 }} action={!settings && <Button onClick={() => setReload(value => value + 1)}>重试</Button>} />}
      <Spin spinning={loading}>
        <Card style={{ maxWidth: 800 }} variant={embedded ? 'borderless' : 'outlined'} styles={embedded ? { body: { padding: 0 } } : undefined}>
          <Space wrap style={{ marginBottom: 24 }}>
            <Tag color={settings?.hasApiKey ? 'green' : 'default'}>{settings?.hasApiKey ? '已配置查询 Key' : '尚未配置查询 Key'}</Tag>
            {settings && <Typography.Text type="secondary">当前使用{settings.configurationSource === 'database' ? '后台保存的配置' : '环境变量配置'}</Typography.Text>}
          </Space>
          <Form form={form} layout="vertical" onFinish={save} initialValues={{ enabled: true, timeoutSeconds: 5 }} disabled={loading || !settings || saving || testing}
            onValuesChange={() => { setDirty(true); setTestResult(null); }}>
            <Form.Item name="apiUrl" label="岗位查询地址" rules={[{ required: true, message: '请输入完整的岗位查询地址' }, { type: 'url', message: '请输入有效的 HTTP/HTTPS 地址' }]}
              extra="填写完整的岗位接口根地址，例如 https://hr.qingz.xyz/api/integrations/agentchat/v1/jobs。">
              <Input maxLength={1000} />
            </Form.Item>
            <Form.Item name="apiKey" label="查询 Key" rules={settings?.hasApiKey ? [] : [{ required: true, whitespace: true, message: '请输入岗位查询 Key' }]}
              extra={settings?.hasApiKey ? '已保存的密钥不会回显；留空保留现有 Key。' : '请使用伙伴通签发的岗位查询 Key，只需岗位读取权限。推送 Key 在推送记录 → 报名提交中单独配置。'}>
              <Input.Password autoComplete="new-password" maxLength={2000} />
            </Form.Item>
            <Form.Item name="timeoutSeconds" label="请求超时（秒）" rules={[{ required: true, message: '请输入请求超时' }]}><InputNumber min={1} max={15} precision={0} /></Form.Item>
            <Form.Item name="enabled" label="启用实时岗位查询" valuePropName="checked" extra="停用后暂停岗位查询与岗位推送。"><Switch /></Form.Item>
            <Space wrap>
              <Button type="primary" htmlType="submit" icon={<SaveOutlined />} loading={saving}>保存配置</Button>
              <Button icon={<ApiOutlined />} loading={testing} onClick={test}>测试连接</Button>
              <Button icon={<ReloadOutlined />} onClick={() => setReload(value => value + 1)}>重新加载</Button>
            </Space>
          </Form>
          {testResult && <Alert style={{ marginTop: 24 }} showIcon type={testResult.success ? 'success' : 'error'}
            message={testResult.message} description={testResult.success ? `响应耗时 ${testResult.responseTime} ms，招聘中岗位 ${testResult.recruitingCount} 个。测试当前表单，不会自动保存配置。` : undefined} />}
        </Card>
      </Spin>
    </div>
  );
}

export default JobSourceSettings;
